import { useEffect, useState } from 'react';
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogContent,
  Divider,
  IconButton,
  Link,
  Tab,
  Tabs,
  Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import AddTaskIcon from '@mui/icons-material/AddTask';
import CheckIcon from '@mui/icons-material/Check';
import { useTranslation } from 'react-i18next';
import i18n from '../../i18n';
import { formatDate } from '../../i18n/format';
import { competitorsApi } from '../../services/competitors';
import { tasksApi } from '../../services/tasks';
import DomainFavicon from './DomainFavicon';
import type { CompetitorDetailResponse, CompetitorEvent } from '@traaker/shared';
import type { CompetitorMetrics } from './CompetitorMatrixView';

// ─── Shared design tokens (mock parity) — one source of truth so Synthèse,
//     Positionnement cards, and chips are visually identical. ──────────────
const T = {
  surface: '#FAFBFD',
  border: '#E5E8F0',
  text: '#0F172A',
  textDim: '#94A0B8',
  bodySize: 13.5,      // single body font size for ALL prose (was 13 vs 14 mismatch)
  bodyLine: 1.6,
  cardRadius: 12,      // px — consistent rounded-square on every card
  cardPad: 18,         // px
} as const;

const CARD_SX = {
  bgcolor: T.surface,
  border: `1px solid ${T.border}`,
  borderRadius: `${T.cardRadius}px`,
  p: `${T.cardPad}px`,
} as const;

const EVENT_META: Record<string, { labelKey: string; color: string }> = {
  news: { labelKey: 'competitors.event.news', color: '#7C3AED' },
  website: { labelKey: 'competitors.event.website', color: '#0EA5E9' },
  blog: { labelKey: 'competitors.event.blog', color: '#F59E0B' },
  source: { labelKey: 'competitors.event.source', color: '#00897B' },
  cocite: { labelKey: 'competitors.event.cocite', color: '#DB2777' },
  score: { labelKey: 'competitors.event.score', color: '#15803D' },
};

export default function CompetitorDetailModal({
  brandId,
  brandName,
  domain,
  name,
  open,
  metrics,
  onClose,
}: {
  brandId: string;
  brandName: string;
  domain: string | null;
  name: string;
  open: boolean;
  metrics: CompetitorMetrics | null;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const [tab, setTab] = useState(0);
  const [data, setData] = useState<CompetitorDetailResponse | null>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open || !domain) return;
    setLoading(true);
    setData(null);
    setTab(0);
    competitorsApi
      .detail(brandId, domain)
      .then(setData)
      .catch(() => setData({ profile: null, events: [] }))
      .finally(() => setLoading(false));
  }, [open, domain, brandId]);

  const profile = data?.profile?.profile as Record<string, any> | null | undefined;
  const events = data?.events || [];

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, p: 2, pb: 1 }}>
        <DomainFavicon domain={domain ?? undefined} name={name} size={40} />
        <Box sx={{ flex: 1 }}>
          <Typography variant="h6" fontWeight={700}>{name}</Typography>
          <Typography variant="caption" color="text.secondary">{domain}</Typography>
        </Box>
        <IconButton onClick={onClose} size="small"><CloseIcon /></IconButton>
      </Box>
      <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ px: 2 }}>
        <Tab label={t('competitors.modal.tabDetails')} />
        <Tab label={events.length ? t('competitors.modal.tabEvolutionCount', { count: events.length }) : t('competitors.modal.tabEvolution')} />
      </Tabs>
      <Divider />
      <DialogContent sx={{ minHeight: 280 }}>
        {loading ? (
          <Box sx={{ textAlign: 'center', py: 6 }}><CircularProgress size={28} /></Box>
        ) : tab === 0 ? (
          <DetailsPane
            profile={profile}
            hasData={!!data?.profile}
            profileStatus={data?.profile?.profileStatus}
            metrics={metrics}
            name={name}
            brandName={brandName}
            brandId={brandId}
          />
        ) : (
          <EvolutionPane events={events} />
        )}
      </DialogContent>
    </Dialog>
  );
}

// ─── Mock-parity section primitives ────────────────────────────────────────
// Eyebrow = small numbered uppercase label above a section title (mock §design).

function SectionHead({ num, eyebrow, title, sub }: { num: number; eyebrow: string; title: string; sub?: string }) {
  return (
    <Box sx={{ mb: 1.75 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <Box
          sx={{
            width: 18, height: 18, borderRadius: '5px', bgcolor: '#F1F5F9',
            border: '1px solid', borderColor: 'divider',
            display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
            fontSize: 10, fontWeight: 700, color: 'text.secondary',
          }}
        >
          {num}
        </Box>
        <Typography
          sx={{ fontSize: 10, fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: '#94A0B8' }}
        >
          {eyebrow}
        </Typography>
      </Box>
      <Typography sx={{ fontSize: 15, fontWeight: 700, letterSpacing: '-0.01em', mt: 0.5, color: '#0F172A' }}>
        {title}
      </Typography>
      {sub && (
        <Typography sx={{ fontSize: 12, color: 'text.secondary', mt: 0.25 }}>{sub}</Typography>
      )}
    </Box>
  );
}

function Spark({ points, color }: { points: number[]; color: string }) {
  if (!points || points.length < 2) return <Box sx={{ height: 24 }} />;
  const w = 120, h = 24, max = Math.max(...points, 1);
  const step = w / (points.length - 1);
  const d = points.map((p, i) => `${i === 0 ? 'M' : 'L'}${(i * step).toFixed(1)},${(h - (p / max) * h).toFixed(1)}`).join(' ');
  return <svg width="100%" height={h} viewBox={`0 0 ${w} ${h}`} preserveAspectRatio="none"><path d={d} fill="none" stroke={color} strokeWidth={1.5} /></svg>;
}

function ScoreCell({ axis, value, sub, color, trend }: { axis: string; value: string; sub: string; color: string; trend?: number[] }) {
  return (
    <Box sx={{ flex: 1, px: 3, py: 2.25, borderRight: '1px solid', borderColor: 'divider', '&:last-of-type': { borderRight: 0 } }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
        <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: color }} />
        <Typography sx={{ color, fontWeight: 700, fontSize: 10, textTransform: 'uppercase', letterSpacing: '0.06em' }}>{axis}</Typography>
      </Box>
      <Typography sx={{ fontSize: 28, fontWeight: 700, letterSpacing: '-0.02em', lineHeight: 1.1, mt: 0.75, color }}>{value}</Typography>
      <Typography sx={{ fontSize: 12, color: 'text.secondary', mt: 0.25, display: 'block' }}>{sub}</Typography>
      {trend && trend.length >= 2 && <Box sx={{ mt: 1.25 }}><Spark points={trend} color={color} /></Box>}
    </Box>
  );
}

function DetailsPane({
  profile,
  hasData,
  profileStatus,
  metrics,
  name,
  brandName,
  brandId,
}: {
  profile?: Record<string, any> | null;
  hasData: boolean;
  profileStatus?: 'ok' | 'failed';
  metrics: CompetitorMetrics | null;
  name: string;
  brandName: string;
  brandId: string;
}) {
  const { t } = useTranslation();
  // Numbering adapts to which sections render (positioning may be absent).
  let n = 0;
  return (
    <Box>
      {/* Score bandeau (3 axes) — full-width hero, flush to the dialog edges */}
      {metrics && (
        <Box
          sx={{
            display: 'flex', bgcolor: '#FAFBFD', borderTop: '1px solid', borderBottom: '1px solid',
            borderColor: 'divider', mx: -3, mt: -3, mb: 3,
          }}
        >
          <ScoreCell
            axis="Referral" color="#B45309"
            value={metrics.referral.pct !== null ? `${metrics.referral.pct}%` : '—'}
            sub={
              t('competitors.modal.score.recommended', {
                on: metrics.referral.recommendedOn,
                count: metrics.referral.promptCount,
              }) +
              (metrics.referral.avgPosition != null
                ? t('competitors.modal.score.avgPositionSuffix', { position: metrics.referral.avgPosition })
                : '')
            }
            trend={metrics.referral.trend}
          />
          <ScoreCell
            axis="Authority" color="#047857"
            value={metrics.authority.pct !== null ? `${metrics.authority.pct}%` : '—'}
            sub={t('competitors.modal.score.present', {
              on: metrics.authority.presentOn,
              count: metrics.authority.promptCount,
            })}
          />
        </Box>
      )}

      {/* Synthèse */}
      {metrics && (
        <Box sx={{ mb: 3 }}>
          <SectionHead num={++n} eyebrow={t('competitors.modal.synthesis.eyebrow')} title={t('competitors.modal.synthesis.title', { name })} />
          <Box sx={{ ...CARD_SX, borderLeft: '3px solid #FF6B35' }}>
            <Typography sx={{ fontSize: T.bodySize, lineHeight: T.bodyLine, color: T.text }}>
              {synthesize(name, brandName, metrics)}
            </Typography>
          </Box>
        </Box>
      )}

      {/* Positionnement stratégique */}
      <Box sx={{ mb: 3 }}>
        <SectionHead
          num={++n}
          eyebrow={t('competitors.modal.positioning.eyebrow')}
          title={t('competitors.modal.positioning.title')}
          sub={t('competitors.modal.positioning.sub')}
        />
        {hasData && profile ? (
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 1.5 }}>
            {profile.description && (
              <PosCard title={t('competitors.modal.positioning.positioning')} badge={t('competitors.modal.badge.observed')} badgeColor="#1E40AF" sx={{ gridColumn: '1 / -1' }}>
                <Typography sx={{ fontSize: T.bodySize, lineHeight: T.bodyLine, color: T.text }}>{String(profile.description)}</Typography>
              </PosCard>
            )}
            <ChipCard title={t('competitors.modal.positioning.differentiators')} badge={t('competitors.modal.badge.observed')} badgeColor="#1E40AF" items={profile.differentiators} color="#B45309" />
            <ChipCard title={t('competitors.modal.positioning.targetQueries')} badge={t('competitors.modal.badge.inferred')} badgeColor="#6B21A8" items={profile.targetQueries} color="#7C3AED" />
            <ChipCard title={t('competitors.modal.positioning.contentPillars')} badge={t('competitors.modal.badge.inferred')} badgeColor="#6B21A8" items={profile.contentPillars} color="#00897B" />
            <ChipCard title={t('competitors.modal.positioning.keywords')} badge={t('competitors.modal.badge.observed')} badgeColor="#1E40AF" items={profile.keywords} />
          </Box>
        ) : (
          <Box sx={{ ...CARD_SX, bgcolor: '#FFF7ED', border: '1px solid #FED7AA' }}>
            <Typography sx={{ fontSize: T.bodySize, color: '#9A3412', lineHeight: T.bodyLine }}>
              {profileStatus === 'failed'
                ? t('competitors.modal.positioning.unavailable')
                : t('competitors.modal.positioning.analyzing')}
            </Typography>
          </Box>
        )}
      </Box>

      {/* Opportunités */}
      {metrics && metrics.opportunities.length > 0 && (
        <Box>
          <SectionHead num={++n} eyebrow={t('competitors.modal.opportunities.eyebrow')} title={t('competitors.modal.opportunities.title', { count: metrics.opportunities.length, name })} />
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            {metrics.opportunities.map((o, i) => (
              <OpportunityRow key={i} opp={o} competitor={name} brandId={brandId} />
            ))}
          </Box>
        </Box>
      )}
    </Box>
  );
}

function PosCard({ title, badge, badgeColor, children, sx }: { title: string; badge: string; badgeColor: string; children: React.ReactNode; sx?: object }) {
  return (
    <Box sx={{ ...CARD_SX, ...sx }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 1.5 }}>
        <Typography sx={{ fontSize: 10, textTransform: 'uppercase', fontWeight: 700, letterSpacing: '0.06em', color: T.textDim }}>{title}</Typography>
        <Chip label={badge} size="small" sx={{ height: 17, fontSize: 9, fontWeight: 700, borderRadius: '4px', bgcolor: `${badgeColor}18`, color: badgeColor, '& .MuiChip-label': { px: 0.75 } }} />
      </Box>
      {children}
    </Box>
  );
}

function ChipCard({ title, badge, badgeColor, items, color }: { title: string; badge: string; badgeColor: string; items: any; color?: string }) {
  if (!Array.isArray(items) || items.length === 0) return null;
  return (
    <PosCard title={title} badge={badge} badgeColor={badgeColor}>
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
        {items.map((v: any, i: number) => (
          <Chip
            key={i}
            label={String(v)}
            size="small"
            sx={{
              height: 26, fontSize: 12, fontWeight: 500, borderRadius: '8px',
              bgcolor: color ? `${color}14` : '#FFFFFF',
              color: color || T.text,
              border: '1px solid',
              borderColor: color ? `${color}30` : T.border,
              '& .MuiChip-label': { px: 1.25 },
            }}
          />
        ))}
      </Box>
    </PosCard>
  );
}

function OpportunityRow({ opp, competitor, brandId }: { opp: CompetitorMetrics['opportunities'][0]; competitor: string; brandId: string }) {
  const { t } = useTranslation();
  const [created, setCreated] = useState(false);
  const [busy, setBusy] = useState(false);
  const isAttack = opp.kind === 'attack';
  const create = async () => {
    setBusy(true);
    try {
      await tasksApi.create({
        brandId,
        title: isAttack
          ? t('competitors.modal.opportunities.taskTitleAttack', { prompt: opp.prompt, competitor })
          : t('competitors.modal.opportunities.taskTitleDefend', { prompt: opp.prompt, competitor }),
        description: opp.detail,
        category: 'content',
        priority: isAttack ? 'high' : 'medium',
        sourceType: 'audit_brand',
        sourceDimension: 'brand_referral',
      });
      setCreated(true);
    } catch {
      /* surfaced by the page-level error elsewhere; keep the modal quiet */
    } finally {
      setBusy(false);
    }
  };
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, ...CARD_SX, p: 1.5 }}>
      <Chip
        label={isAttack ? t('competitors.modal.opportunities.attack') : t('competitors.modal.opportunities.defend')}
        size="small"
        sx={{ height: 20, fontSize: 10, fontWeight: 700, borderRadius: '5px', bgcolor: isAttack ? '#FEE2E2' : '#E0F2FE', color: isAttack ? '#B91C1C' : '#075985' }}
      />
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography variant="body2" fontWeight={600} noWrap title={opp.prompt}>{opp.prompt}</Typography>
        <Typography variant="caption" color="text.secondary">{opp.detail}</Typography>
      </Box>
      <Button
        size="small"
        variant={created ? 'text' : 'outlined'}
        disabled={busy || created}
        startIcon={created ? <CheckIcon /> : <AddTaskIcon />}
        onClick={create}
        sx={{ whiteSpace: 'nowrap', flexShrink: 0 }}
      >
        {created ? t('competitors.modal.opportunities.taskCreated') : t('competitors.modal.opportunities.createTask')}
      </Button>
    </Box>
  );
}

/** Deterministic synthesis from the metrics (no LLM). */
function synthesize(name: string, brandName: string, m: CompetitorMetrics): string {
  const parts: string[] = [];
  if (m.referral.pct !== null) {
    parts.push(
      i18n.t('competitors.synthesis.referral', { name, on: m.referral.recommendedOn, count: m.referral.promptCount }) +
      (m.referral.avgPosition != null ? i18n.t('competitors.synthesis.referralAvgPosition', { position: m.referral.avgPosition }) : ''),
    );
  }
  if (m.authority.pct !== null && m.authority.promptCount > 0) {
    parts.push(i18n.t('competitors.synthesis.authority', { on: m.authority.presentOn, count: m.authority.promptCount }));
  }
  if (m.narrative.coCited > 0) {
    parts.push(i18n.t('competitors.synthesis.narrative', { brandName, count: m.narrative.coCited }));
  }
  if (parts.length === 0) return i18n.t('competitors.synthesis.none', { name });
  const attacks = m.opportunities.filter((o) => o.kind === 'attack').length;
  const tail = attacks > 0 ? i18n.t('competitors.synthesis.attackTail', { count: attacks }) : '';
  return parts.join(', ') + '.' + tail;
}

function EvolutionPane({ events }: { events: CompetitorEvent[] }) {
  const { t } = useTranslation();
  if (events.length === 0) {
    return (
      <Typography variant="body2" color="text.secondary" sx={{ py: 4, textAlign: 'center' }}>
        {t('competitors.modal.evolution.empty')}
      </Typography>
    );
  }
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      {events.map((e, i) => {
        const meta = EVENT_META[e.kind];
        const label = meta ? t(meta.labelKey) : e.kind;
        const color = meta?.color || '#5B6479';
        return (
          <Box key={i} sx={{ display: 'flex', gap: 1.5 }}>
            <Box sx={{ width: 4, borderRadius: 2, bgcolor: color, flexShrink: 0 }} />
            <Box sx={{ flex: 1 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                <Chip label={label} size="small" sx={{ bgcolor: `${color}18`, color, fontWeight: 700, height: 20, fontSize: 10 }} />
                {e.diffPct != null && (
                  <Typography variant="caption" color="text.secondary">{t('competitors.modal.evolution.diff', { pct: e.diffPct })}</Typography>
                )}
                <Typography variant="caption" color="text.secondary" sx={{ ml: 'auto' }}>
                  {formatDate(e.createdAt)}
                </Typography>
              </Box>
              <Typography variant="body2" fontWeight={600}>{e.title}</Typography>
              {e.detail && (
                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>{e.detail}</Typography>
              )}
              {e.sourceUrl && (
                <Link href={e.sourceUrl} target="_blank" rel="noopener" variant="caption" sx={{ mt: 0.5, display: 'inline-block' }}>
                  {t('competitors.modal.evolution.source')}
                </Link>
              )}
            </Box>
          </Box>
        );
      })}
    </Box>
  );
}
